import axiosInstance from '../../../shared/api/axiosInstance';
import type { SequenceAnalytics } from '../../../generated/api/models/sequenceAnalytics';
import type {
  EmailTemplate,
  EmailTemplateCreate,
  EmailTemplateUpdate,
  Sequence,
  SequenceCreate,
  SequenceUpdate,
  SequenceStep,
  SequenceStepCreate,
  SequenceStepUpdate,
  SequenceStepVariant,
  SequenceStepVariantCreate,
  SequenceStepVariantUpdate,
  Enrollment,
  OutreachTask,
  ProspectResearch,
  GenerateEmail,
  GenerateEmailRequest,
  GenerateSequenceRequest,
  ListSequencesParams,
  ListEmailTemplatesParams,
  ListOutreachTasksParams,
  ListEnrollmentsParams,
} from '../types/prospecting';

const BASE_URL = '/api/v1/pipeline/prospecting';

// ============================================================================
// Email Templates
// ============================================================================

export const emailTemplateApi = {
  list: async (params?: ListEmailTemplatesParams): Promise<EmailTemplate[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/templates`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<EmailTemplate> => {
    const response = await axiosInstance.get(`${BASE_URL}/templates/${id}`);
    return response.data.data;
  },

  create: async (data: EmailTemplateCreate): Promise<EmailTemplate> => {
    const response = await axiosInstance.post(`${BASE_URL}/templates`, data);
    return response.data.data;
  },

  update: async (id: string, data: EmailTemplateUpdate): Promise<EmailTemplate> => {
    const response = await axiosInstance.put(`${BASE_URL}/templates/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/templates/${id}`);
  },
};

// ============================================================================
// Sequences
// ============================================================================

export const sequenceApi = {
  list: async (params?: ListSequencesParams): Promise<Sequence[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/sequences`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<Sequence> => {
    const response = await axiosInstance.get(`${BASE_URL}/sequences/${id}`);
    return response.data.data;
  },

  create: async (data: SequenceCreate): Promise<Sequence> => {
    const response = await axiosInstance.post(`${BASE_URL}/sequences`, data);
    return response.data.data;
  },

  update: async (id: string, data: SequenceUpdate): Promise<Sequence> => {
    const response = await axiosInstance.put(`${BASE_URL}/sequences/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/sequences/${id}`);
  },

  changeStatus: async (id: string, status: string): Promise<Sequence> => {
    const response = await axiosInstance.patch(`${BASE_URL}/sequences/${id}/status`, { status });
    return response.data.data;
  },

  duplicate: async (id: string): Promise<Sequence> => {
    const response = await axiosInstance.post(`${BASE_URL}/sequences/${id}/duplicate`);
    return response.data.data;
  },
};

// ============================================================================
// Sequence Steps
// ============================================================================

export const sequenceStepApi = {
  list: async (sequenceId: string): Promise<SequenceStep[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/sequences/${sequenceId}/steps`);
    return response.data.data;
  },

  create: async (sequenceId: string, data: SequenceStepCreate): Promise<SequenceStep> => {
    const response = await axiosInstance.post(`${BASE_URL}/sequences/${sequenceId}/steps`, data);
    return response.data.data;
  },

  update: async (sequenceId: string, stepId: string, data: SequenceStepUpdate): Promise<SequenceStep> => {
    const response = await axiosInstance.put(`${BASE_URL}/sequences/${sequenceId}/steps/${stepId}`, data);
    return response.data.data;
  },

  delete: async (sequenceId: string, stepId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/sequences/${sequenceId}/steps/${stepId}`);
  },

  reorder: async (sequenceId: string, stepIds: string[]): Promise<SequenceStep[]> => {
    const response = await axiosInstance.put(`${BASE_URL}/sequences/${sequenceId}/steps/reorder`, { step_ids: stepIds });
    return response.data.data;
  },
};

// ============================================================================
// Step Variants
// ============================================================================

export const stepVariantApi = {
  list: async (stepId: string): Promise<SequenceStepVariant[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/steps/${stepId}/variants`);
    return response.data.data;
  },

  create: async (stepId: string, data: SequenceStepVariantCreate): Promise<SequenceStepVariant> => {
    const response = await axiosInstance.post(`${BASE_URL}/steps/${stepId}/variants`, data);
    return response.data.data;
  },

  update: async (variantId: string, data: SequenceStepVariantUpdate): Promise<SequenceStepVariant> => {
    const response = await axiosInstance.put(`${BASE_URL}/variants/${variantId}`, data);
    return response.data.data;
  },

  delete: async (variantId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/variants/${variantId}`);
  },
};

// ============================================================================
// Enrollments
// ============================================================================

export const enrollmentApi = {
  list: async (sequenceId: string, params?: ListEnrollmentsParams): Promise<Enrollment[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/sequences/${sequenceId}/enrollments`, { params });
    return response.data.data;
  },

  get: async (enrollmentId: string): Promise<Enrollment> => {
    const response = await axiosInstance.get(`${BASE_URL}/enrollments/${enrollmentId}`);
    return response.data.data;
  },

  enroll: async (sequenceId: string, contactIds: string[]): Promise<Enrollment[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/sequences/${sequenceId}/enroll`, { contact_ids: contactIds });
    return response.data.data;
  },

  changeStatus: async (enrollmentId: string, status: string): Promise<Enrollment> => {
    const response = await axiosInstance.patch(`${BASE_URL}/enrollments/${enrollmentId}/status`, { status });
    return response.data.data;
  },

  getContactEnrollments: async (contactId: string): Promise<Enrollment[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/contacts/${contactId}/enrollments`);
    return response.data.data;
  },
};

// ============================================================================
// Analytics
// ============================================================================

export const sequenceAnalyticsApi = {
  // Returns the typed payload directly, not the `{ data }` envelope the older
  // prospecting routes use — this route declares a response_model.
  get: async (sequenceId: string): Promise<SequenceAnalytics> => {
    const response = await axiosInstance.get(`${BASE_URL}/analytics/sequences/${sequenceId}`);
    return response.data;
  },
};

// ============================================================================
// Outreach Tasks
// ============================================================================

export const outreachTaskApi = {
  list: async (params?: ListOutreachTasksParams): Promise<OutreachTask[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tasks`, { params });
    return response.data.data;
  },

  getToday: async (): Promise<OutreachTask[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tasks/today`);
    return response.data.data;
  },

  changeStatus: async (taskId: string, status: string, outcome?: string, outcomeNotes?: string): Promise<OutreachTask> => {
    const response = await axiosInstance.patch(`${BASE_URL}/tasks/${taskId}/status`, {
      status,
      outcome,
      outcome_notes: outcomeNotes,
    });
    return response.data.data;
  },

  updateOutcome: async (taskId: string, outcome: string, outcomeNotes?: string): Promise<OutreachTask> => {
    const response = await axiosInstance.patch(`${BASE_URL}/tasks/${taskId}/outcome`, {
      outcome,
      outcome_notes: outcomeNotes,
    });
    return response.data.data;
  },
};

// ============================================================================
// Prospect Research
// ============================================================================

export const researchApi = {
  getContactResearch: async (contactId: string): Promise<ProspectResearch | null> => {
    const response = await axiosInstance.get(`${BASE_URL}/research/contact/${contactId}`);
    return response.data.data;
  },

  getOrganizationResearch: async (organizationId: string): Promise<ProspectResearch | null> => {
    const response = await axiosInstance.get(`${BASE_URL}/research/organization/${organizationId}`);
    return response.data.data;
  },

  researchContact: async (contactId: string): Promise<ProspectResearch> => {
    const response = await axiosInstance.post(`${BASE_URL}/research/contact/${contactId}`);
    return response.data.data;
  },

  researchCompany: async (organizationId: string): Promise<ProspectResearch> => {
    const response = await axiosInstance.post(`${BASE_URL}/research/organization/${organizationId}`);
    return response.data.data;
  },
};

// ============================================================================
// AI Copy Generation
// ============================================================================

export const aiCopyApi = {
  generateEmail: async (data: GenerateEmailRequest): Promise<GenerateEmail> => {
    const response = await axiosInstance.post(`${BASE_URL}/ai/generate-email`, data);
    return response.data.data;
  },

  generateSequence: async (data: GenerateSequenceRequest): Promise<Record<string, unknown>[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/ai/generate-sequence`, data);
    return response.data.data;
  },
};
